/**
 * The Turnstile widget, loaded only when someone opens the sign-in form.
 *
 * Deliberately not bundled and not loaded on page load. This is a watch-order
 * guide that works offline; the overwhelming majority of visits never touch
 * the account dialog, and making all of them pay for a third-party script —
 * in bytes, in a DNS lookup, and in a request to Cloudflare that reveals a
 * visit — to protect an endpoint they will never call is the wrong trade.
 *
 * If the script cannot load (offline, blocked by an extension, ad blocker),
 * every function here fails soft and returns null. The server decides what an
 * absent token means; the client's job is not to pretend it has one.
 */

const SCRIPT_URL = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

interface TurnstileApi {
  render: (
    el: HTMLElement,
    opts: {
      sitekey: string;
      callback: (token: string) => void;
      "error-callback"?: () => void;
      "expired-callback"?: () => void;
      theme?: "auto" | "light" | "dark";
    },
  ) => string;
  reset: (id?: string) => void;
  remove: (id?: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let loading: Promise<TurnstileApi | null> | null = null;

/** Loads the script once per page; concurrent callers share the same promise. */
export function loadTurnstile(): Promise<TurnstileApi | null> {
  if (typeof document === "undefined") return Promise.resolve(null);
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (loading) return loading;

  loading = new Promise((resolve) => {
    const script = document.createElement("script");
    script.src = SCRIPT_URL;
    script.async = true;
    script.onload = () => resolve(window.turnstile ?? null);
    // Fail soft: a blocked script must not make the form unusable. The request
    // goes out without a token and the server rejects it with a message the
    // reader can act on, which beats a spinner that never resolves.
    script.onerror = () => resolve(null);
    document.head.appendChild(script);
  });
  return loading;
}

/** The sitekey for this deployment, or null when no challenge is configured. */
export async function fetchSitekey(): Promise<string | null> {
  try {
    const res = await fetch("/api/auth/config", { credentials: "same-origin" });
    if (!res.ok) return null;
    return ((await res.json()) as { turnstileSitekey: string | null }).turnstileSitekey;
  } catch {
    return null;
  }
}
